import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSave, FiUpload, FiTrash2, FiHash, FiRotateCcw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Spinner } from '../../components/common';
import CmsField from '../cms/components/CmsField';
import { billingSettingsService } from '../../services/billingSettingsService';
import { billingFiles } from '../../services/billingFiles';
import { getApiError, getApiFieldErrors } from '../../services/cmsService';
import { financialYearOptions, normalizeGstin } from './billingUtils';
import './Billing.css';
import './BillingSettingsPage.css';

const GROUPS = [
  { key: 'company', title: 'Company', subtitle: 'Printed in the invoice header.' },
  { key: 'bank', title: 'Bank details', subtitle: 'Printed in the payment section of every invoice.' },
  { key: 'tax', title: 'Tax defaults', subtitle: 'Used when a draft doesn’t set its own values.' },
  { key: 'numbering', title: 'Numbering format' },
  { key: 'terms', title: 'Terms & conditions', subtitle: 'One term per line; the PDF numbers them.' },
];

const IMAGE_HINTS = {
  company_logo: 'PNG, JPG or WebP up to 2 MB. Without an upload the built-in logo is used.',
  seal_signature_image: 'A transparent PNG looks best. Until one is uploaded the seal box prints empty.',
};

const textValues = (fields) =>
  Object.values(fields)
    .flat()
    .filter((f) => f.type !== 'image')
    .reduce((acc, f) => ({ ...acc, [f.key]: f.value ?? '' }), {});

/** An uploaded image setting, previewed through a token-authenticated blob URL. */
const ImageSetting = ({ field, onChanged }) => {
  const inputRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Replacing an image leaves `has_image` true, so bump this to refetch the preview.
  const [version, setVersion] = useState(0);
  const hasPreview = field.has_image || field.key === 'company_logo';

  useEffect(() => {
    if (!hasPreview) {
      setUrl(null);
      return undefined;
    }
    let objectUrl = null;
    let cancelled = false;
    billingFiles
      .imageUrl(field.key)
      .then((next) => {
        if (cancelled) URL.revokeObjectURL(next);
        else {
          objectUrl = next;
          setUrl(next);
        }
      })
      .catch(() => !cancelled && setUrl(null));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [field.key, hasPreview, version]);

  const upload = async (file) => {
    if (!file) return;
    setError('');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setError('Use a PNG, JPG or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('The image is larger than 2 MB.');
      return;
    }
    setBusy(true);
    try {
      onChanged(await billingSettingsService.uploadImage(field.key, file));
      setVersion((v) => v + 1);
      toast.success(`${field.label} uploaded`);
    } catch (err) {
      setError(getApiFieldErrors(err).image || getApiError(err, 'Upload failed'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      onChanged(await billingSettingsService.deleteImage(field.key));
      setVersion((v) => v + 1);
      toast.success(`${field.label} removed`);
    } catch (err) {
      toast.error(getApiError(err, 'Could not remove the image'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bs-image">
      <span className="cf-label">{field.label}</span>
      <div className="bs-image-row">
        <div className={`bs-image-preview ${field.key === 'seal_signature_image' ? 'is-seal' : ''}`}>
          {url ? <img src={url} alt={field.label} /> : <span className="bl-muted">No image</span>}
        </div>
        <div className="bs-image-actions">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => upload(e.target.files?.[0])} />
          <Button size="small" variant="outline" icon={<FiUpload />} loading={busy} onClick={() => inputRef.current?.click()}>
            {field.has_image ? 'Replace' : 'Upload'}
          </Button>
          {field.has_image && (
            <Button size="small" variant="ghost" icon={<FiTrash2 />} onClick={remove} disabled={busy}>
              {field.key === 'company_logo' ? 'Use default' : 'Remove'}
            </Button>
          )}
          <span className="cf-hint">{IMAGE_HINTS[field.key]}</span>
          {error && <span className="cf-error">{error}</span>}
        </div>
      </div>
    </div>
  );
};

const BillingSettingsPage = () => {
  const [data, setData] = useState(null);
  const [original, setOriginal] = useState({});
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [sequenceFy, setSequenceFy] = useState('');
  const [sequenceNext, setSequenceNext] = useState('');
  const [sequenceError, setSequenceError] = useState('');
  const [sequenceSaving, setSequenceSaving] = useState(false);

  const apply = (payload, { keepEdits = false } = {}) => {
    const fresh = textValues(payload.fields);
    setData(payload);
    setOriginal(fresh);
    // Image changes return the whole payload; don't throw away unsaved text edits.
    setValues((prev) => (keepEdits ? { ...fresh, ...prev } : fresh));
  };

  useEffect(() => {
    billingSettingsService
      .get()
      .then((payload) => {
        apply(payload);
        setSequenceFy(payload.numbering.financial_year);
        setSequenceNext(String(payload.numbering.next_number));
      })
      .catch((error) => toast.error(getApiError(error, 'Failed to load billing settings')));
  }, []);

  const changedKeys = useMemo(
    () => Object.keys(values).filter((key) => String(values[key] ?? '') !== String(original[key] ?? '')),
    [values, original]
  );

  const setValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const save = async () => {
    if (!changedKeys.length) return;
    setSaving(true);
    setErrors({});
    try {
      const changes = Object.fromEntries(changedKeys.map((key) => [key, values[key]]));
      apply(await billingSettingsService.save(changes));
      toast.success('Billing settings saved');
    } catch (error) {
      const api = getApiFieldErrors(error);
      setErrors(
        Object.fromEntries(Object.entries(api).map(([key, msg]) => [key.replace(/^settings\./, ''), msg]))
      );
      toast.error(getApiError(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const saveSequence = async () => {
    setSequenceError('');
    const next = Number(sequenceNext);
    if (!Number.isInteger(next) || next < 1) {
      setSequenceError('Enter a whole number of 1 or more');
      return;
    }
    setSequenceSaving(true);
    try {
      const payload = await billingSettingsService.setSequence(sequenceFy, next);
      apply(payload, { keepEdits: true });
      toast.success(`Numbering for ${sequenceFy} continues from ${next}`);
    } catch (error) {
      const api = getApiFieldErrors(error);
      setSequenceError(api.next_number || api.financial_year || getApiError(error, 'Could not update numbering'));
    } finally {
      setSequenceSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="bl-center">
        <Spinner size="large" />
      </div>
    );
  }

  const renderField = (field) => {
    if (field.type === 'image') {
      return <ImageSetting key={field.key} field={field} onChanged={(payload) => apply(payload, { keepEdits: true })} />;
    }

    const common = {
      id: `bs-${field.key}`,
      label: field.label,
      error: errors[field.key],
      disabled: field.read_only,
    };

    if (field.type === 'boolean') {
      return (
        <CmsField
          key={field.key}
          {...common}
          type="boolean"
          value={values[field.key] === true || values[field.key] === '1' || values[field.key] === 1}
          onChange={(checked) => setValue(field.key, checked ? '1' : '0')}
        />
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.key} className="bl-span-all">
          <CmsField
            {...common}
            type="textarea"
            rows={field.key === 'terms_and_conditions' ? 6 : 3}
            value={values[field.key]}
            onChange={(v) => setValue(field.key, v)}
          />
        </div>
      );
    }

    return (
      <CmsField
        key={field.key}
        {...common}
        type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
        value={values[field.key]}
        onChange={(v) => setValue(field.key, field.type === 'gstin' ? normalizeGstin(v).slice(0, 15) : v)}
        hint={field.read_only ? 'Set automatically from the company GSTIN.' : field.key === 'invoice_number_format' ? 'Must contain {SEQ}. Optional: {YY}, {YYYY}.' : undefined}
        inputProps={field.type === 'gstin' ? { maxLength: 15, spellCheck: false } : undefined}
      />
    );
  };

  const numbering = data.numbering;
  const fyOptions = Array.from(new Set([numbering.financial_year, ...financialYearOptions(5)]));

  return (
    <div className="bl-page billing-settings">
      <div className="bs-savebar">
        <div>
          <h1 className="bs-title">Billing Settings</h1>
          <p className="bl-muted">Company, bank and tax details printed on every invoice. Issued invoices keep the values they were issued with.</p>
        </div>
        <div className="bl-header-actions">
          {changedKeys.length > 0 && (
            <Button variant="ghost" icon={<FiRotateCcw />} onClick={() => { setValues(original); setErrors({}); }} disabled={saving}>
              Discard
            </Button>
          )}
          <Button icon={<FiSave />} onClick={save} loading={saving} disabled={!changedKeys.length}>
            {changedKeys.length ? `Save ${changedKeys.length} change${changedKeys.length === 1 ? '' : 's'}` : 'Saved'}
          </Button>
        </div>
      </div>

      {GROUPS.filter((g) => data.fields[g.key]?.length).map((group) => (
        <Card key={group.key} title={group.title} subtitle={group.subtitle}>
          <div className="bl-form-grid">
            {data.fields[group.key].filter((f) => f.type !== 'image').map(renderField)}
          </div>
          {data.fields[group.key].some((f) => f.type === 'image') && (
            <div className="bs-images">
              {data.fields[group.key].filter((f) => f.type === 'image').map(renderField)}
            </div>
          )}
        </Card>
      ))}

      <Card title="Invoice numbering" subtitle="Where the sequence continues in each financial year" icon={<FiHash />}>
        <div className="bs-numbering">
          <div className="bs-next">
            <span className="bl-stat-label">Next invoice · FY {numbering.financial_year}</span>
            <span className="bs-next-value">{numbering.next_invoice_number}</span>
          </div>

          <div className="bl-table-wrap">
            <table className="bl-table">
              <thead>
                <tr>
                  <th>Financial year</th>
                  <th className="num">Last number used</th>
                </tr>
              </thead>
              <tbody>
                {numbering.sequences.length === 0 ? (
                  <tr><td colSpan={2} className="bl-muted">No invoices numbered yet.</td></tr>
                ) : (
                  numbering.sequences.map((s) => (
                    <tr key={s.financial_year}>
                      <td>{s.financial_year}</td>
                      <td className="num">{s.last_number}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="bs-sequence-form">
            <h4>Set starting number</h4>
            <p className="bl-muted">
              Use this when invoices were numbered by hand, e.g. set 2025-26 to start at 19 after #25/18.
              It can’t go back to a number that has already been issued.
            </p>
            <div className="bs-sequence-row">
              <CmsField
                id="bs-seq-fy"
                type="select"
                label="Financial year"
                options={fyOptions.map((fy) => ({ value: fy, label: fy }))}
                value={sequenceFy}
                onChange={setSequenceFy}
              />
              <CmsField
                id="bs-seq-next"
                type="number"
                label="Next number"
                value={sequenceNext}
                onChange={(v) => {
                  setSequenceNext(v);
                  setSequenceError('');
                }}
                error={sequenceError}
                inputProps={{ min: 1, step: 1 }}
              />
              <Button variant="secondary" onClick={saveSequence} loading={sequenceSaving}>
                Set
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BillingSettingsPage;
